// Recent-builds list behind the build stamp (Maciek 20.10, PreLaunchChecklist 2.15).
//
// Pure helpers shared by vite.config.ts (which runs git at build time) and the app (which renders
// the list). No Node or DOM imports: vite.config.ts is type-checked under tsconfig.node, the app
// under tsconfig.app, and this file has to satisfy both.

/** One commit as shown in the „Dev · recent builds” panel. */
export interface BuildLogEntry {
  /** Short hash, 7 characters — the same length as the build stamp. */
  hash: string;
  /** ISO 8601 commit date. */
  date: string;
  author: string;
  subject: string;
  /** More than one parent. */
  merge: boolean;
}

const POLE = '\x1f';
const REKORD = '\x1e';

/** How many commits the panel lists. */
export const BUILD_LOG_LIMIT = 40;

/**
 * `git log` arguments. All commits, not `--first-parent`, so a merged branch's own commits show up;
 * merges stay in and are marked. Full hash + parents, sliced/counted in `parseGitLog`, because `%h`
 * grows past 7 characters when a prefix becomes ambiguous.
 */
export const GIT_LOG_ARGS = [
  'log',
  '-n',
  String(BUILD_LOG_LIMIT),
  `--format=%H${POLE}%P${POLE}%cI${POLE}%an${POLE}%s${REKORD}`,
];

export function parseGitLog(raw: string): BuildLogEntry[] {
  return raw
    .split(REKORD)
    .map((r) => r.trim())
    .filter(Boolean)
    .flatMap((r) => {
      const [hash, parents, date, author, subject] = r.split(POLE);
      if (!hash || !date) return [];
      return [{
        hash: hash.slice(0, 7),
        date,
        author: author ?? '',
        subject: subject ?? '',
        merge: (parents ?? '').trim().split(/\s+/).filter(Boolean).length > 1,
      }];
    });
}

/**
 * THE LEAK RULE: which list a bundle carries. Commit subjects describe unreleased work, so they
 * ship only in developer builds — never in production (VITE_ENV=production, release.yml) and never
 * from `main`, which deploys stage.flathero.pl. Every other build (maciej-/piotr-/bartek- branches,
 * local dev) gets the list. Any git failure (no git, shallow clone) yields `[]`, never a broken build.
 *
 * `runGit` is only called once the build is known to be allowed the list, so stage and prod builds
 * do not even read history.
 */
export function buildLogToInject(opts: {
  isProductionBuild: boolean;
  branch: string;
  runGit: (args: string[]) => string;
}): BuildLogEntry[] {
  if (opts.isProductionBuild || opts.branch === 'main') return [];
  try {
    return parseGitLog(opts.runGit(GIT_LOG_ARGS));
  } catch {
    return [];
  }
}

const WARSZAWA = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * „20 Oct, 18:35” in Warsaw time, whatever the viewer's or build machine's zone. English on purpose:
 * the dev panels are an internal tool (Conv 2 exception, like /admin). Empty for an unparsable date.
 */
export function formatWarsawTime(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const p = Object.fromEntries(WARSZAWA.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.day} ${p.month}, ${p.hour}:${p.minute}`;
}

/** First word of the author name; „Maciej (Claude)” → „Maciej”. */
export function firstName(author: string): string {
  return author.trim().split(/\s+/)[0] ?? '';
}

/** BUILD_SHA may be longer than 7 locally (`git rev-parse --short` on an ambiguous prefix). */
export function isThisBuild(hash: string, buildSha: string): boolean {
  if (!hash || !buildSha) return false;
  return buildSha.startsWith(hash) || hash.startsWith(buildSha);
}
